import type { MeResponse, NavigationGroupDto } from '@an-crm/shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { effectiveNavigation } from '../shell/navigation';
import { meQuery, navigationQuery } from './queries';
import { canMutate, hasPermission, isReadOnly } from './read-only';

/** Current session (prefetched by the authenticated route loader). */
export function useMe(): MeResponse {
  return useSuspenseQuery(meQuery).data;
}

/** Sidebar navigation as served by the API (+ platform fallback). */
export function useNavigation(): NavigationGroupDto[] {
  const me = useMe();
  const nav = useSuspenseQuery(navigationQuery).data;
  return useMemo(() => effectiveNavigation(nav, me), [nav, me]);
}

/** Read access: whether the user holds a permission (Super Admins hold all). */
export function useHasPermission(permission: string): boolean {
  return hasPermission(useMe(), permission);
}

/** True while in a read-only Super Admin support session. */
export function useReadOnly(): boolean {
  return isReadOnly(useMe());
}

/**
 * Write access: the permission AND not read-only. Use this for every create/edit/delete control so
 * support sessions hide them (the server rejects such writes with FORBIDDEN anyway).
 */
export function useCanMutate(permission?: string): boolean {
  return canMutate(useMe(), permission);
}
